// ── Enums ──
// String values match the backend serialization (enum.ToString())

export enum EstadoRecepcion {
  Pendiente = 'Pendiente',
  Aprobada = 'Aprobada',
  Rechazada = 'Rechazada',
  EnRevision = 'En Revision',
}

export enum ResultadoValidacion {
  Pendiente = 'Pendiente',
  Conforme = 'Conforme',
  Observado = 'Observado',
  NoConforme = 'NoConforme',
}

export enum TipoValidacion {
  Automatica = 0,
  Manual = 1,
}

// ── List ──

export interface RecepcionListItemResponse {
  id: string
  batchCode: string
  supplierRuc: string
  supplierName: string
  supplierType: string
  mineralTypeName: string | null
  loadWeight: number | null
  estado: EstadoRecepcion
  overallResult: ResultadoValidacion | null
  totalValidations: number
  conformantCount: number
  observedCount: number
  nonConformantCount: number
  receivedAt: string
  validatedBy: string | null
  actaType: string | null
  tieneActa: boolean
  tieneVerificationReport: boolean
  hasSamples: boolean
  samplesCount: number
}

// ── Muestreo ──

export type SamplePurpose = 'PlantLab' | 'Supplier' | 'Tiebreaker' | 'Extra'
export type SampleStatus = 'Pendiente' | 'EnProceso' | 'Finalizado'

export interface SampleResponse {
  id: string
  code: string
  batchNumber: number
  sequenceInBatch: number
  purpose: SamplePurpose
  status: SampleStatus
  generatedAt: string
  printedAt: string | null
  printCount: number
}

export interface GenerateSamplesRequest {
  sampleCount?: number
}

export interface GenerateSamplesResponse {
  batchNumber: number
  batchCode: string
  samples: SampleResponse[]
}

// ── Validacion Config ──

export interface ValidacionConfigItem {
  id: string
  nombre: string
  descripcion: string
  tipo: TipoValidacion
  orden: number
}

export interface ValidacionConfigResponse {
  items: ValidacionConfigItem[]
}

// ── Iniciar ──

export interface IniciarRecepcionRequest {
  shipmentRecordId: string
  proveedorRuc: string
}

export interface IniciarRecepcionResponse {
  recepcionId: string
  validaciones: ValidacionConfigItem[]
}

// ── Validar Item ──

export interface ValidarItemResponse {
  validacionId: string
  resultado: ResultadoValidacion
  mensaje: string
  detalles: string | null
}

// ── Resultado ──

export interface ResultadoRecepcionResponse {
  recepcionId: string
  codigoLote: string
  estado: EstadoRecepcion
  resultado: ResultadoValidacion
  totalConformes: number
  totalObservados: number
  totalNoConformes: number
  totalPendientes: number
  validaciones: ResultadoValidacionItem[]
}

export interface ResultadoValidacionItem {
  id: string
  validacionConfigId: string
  nombre: string
  tipo: TipoValidacion
  resultado: ResultadoValidacion | null
  mensaje: string | null
  detalles: string | null
  pendiente: boolean
}

// ── Detalle Validacion ──

export interface DetalleValidacionResponse {
  id: string
  nombre: string
  tipo: TipoValidacion
  resultado: ResultadoValidacion | null
  mensaje: string | null
  snapshot: Record<string, unknown> | null
  creadoEn: string
}

// ── Actualizar Manual ──

export interface ActualizarManualRequest {
  resultado: ResultadoValidacion
  observaciones: string
}

// ── Finalizar ──

export interface FinalizarRecepcionRequest {
  aprobada: boolean
  observaciones: string
}

export interface FinalizarRecepcionResponse {
  actaUrl: string | null
}

// ── Detalle Recepcion ──

export interface DetalleRecepcionResponse {
  id: string
  batchCode: string
  receivedAt: string
  validatedBy: string | null
  estado: EstadoRecepcion
  overallResult: ResultadoValidacion | null
  observations: string | null
  verifiedWeightKg: number | null
  warehouseLocation: string | null
  supplierRuc: string | null
  supplierName: string | null
  supplierType: string | null
  mineralTypeName: string | null
  loadWeight: number | null
  vehiclePlate: string | null
  driverName: string | null
  actaType: string | null
  tieneActa: boolean
  tieneVerificationReport: boolean
  totalValidations: number
  conformantCount: number
  observedCount: number
  nonConformantCount: number
  validations: DetalleReceptionValidationItem[]
  createdAt: string
  updatedAt: string
}

export interface DetalleReceptionValidationItem {
  id: string
  validationConfigId: string
  code: string
  name: string
  description: string | null
  groupName: string
  groupOrder: number
  order: number
  result: ResultadoValidacion
  appliedSeverity: string
  source: string
  observation: string | null
  operatorObservation: string | null
  queriedAt: string
  executionTimeMs: number
  displayCard: DisplayCardDto | null
  dataSourceFetchedAt: string | null
  dataSourceDescription: string | null
}

// Mirrors MineTrace.Shared.Contracts.RecepcionApp.DisplayCardDto. The mobile app
// renders results from this structure; the web detail page consumes the same shape.
export interface DisplayCardDto {
  title: string
  statusLabel: string
  highlight: DisplayCardHighlight | null
  rows: DisplayCardRow[]
}

export interface DisplayCardHighlight {
  label: string
  value: string
  unit: string | null
  variant: 'positive' | 'warning' | 'negative' | 'neutral' | string
}

export interface DisplayCardRow {
  label: string
  value: string
  variant: 'positive' | 'warning' | 'negative' | null | string
}

// ── Lote lookup ──

export interface LoteValidarQrResponse {
  id: string
  batchCode: string
  supplierName: string | null
  supplierRuc: string | null
  mineralTypeName: string | null
  mineralWeight: string | null
  measurementUnitName: string | null
  completedAt: string | null
  shipmentState: number
  collectionCenterName: string | null
  driverName: string | null
  vehiclePlate: string | null
}
